"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Viewer_1 = __importDefault(require("../Viewer"));
const GroupBox_3d_1 = require("./GroupBox.3d");
// eslint-disable-next-line import/no-cycle
const events_1 = require("../../../utils/events");
// eslint-disable-next-line import/no-cycle
const utils_3d_1 = require("../utils/utils.3d");
/*
    In order to work properly collision processor needs relevant data about
    objects' bounding boxes. To include an object into the system use
    "notifyObjectAdded" otherwise it won't be counted while detecting collision

    Every time object changes position/size or other property that would
    influence collision, it's data needs to be updated using
    "notifyObjectUpdated"

    If removing an object, collision system needs to be updated using
    "notifyObjectRemoved"

    Collision test functions return coProIndex type object or an array,
    it can be used to get any data needed about the object, for example it's
    bounding box using getObjBox(coProIndex)

    Collision test functions also include boolean "result" parameter
    that must be checked before trying to access other result data (it
    may be null if "result" parameter is false)
*/
class CollisionProcessor {
    constructor(viewer) {
        this.groupBoxes = [];
        this.viewer = viewer;
    }
    getAiCollisionExcludeIds(primaryObject) {
        var _a, _b, _c;
        const assemblyId = (_a = primaryObject.userData) === null || _a === void 0 ? void 0 : _a.aiAssemblyId;
        const memberId = (_b = primaryObject.userData) === null || _b === void 0 ? void 0 : _b.aiMemberId;
        if (!assemblyId || !memberId) {
            return [];
        }
        const primaryExcludeMemberIds = new Set(Array.isArray((_c = primaryObject.userData) === null || _c === void 0 ? void 0 : _c.aiCollisionExcludeMemberIds)
            ? primaryObject.userData.aiCollisionExcludeMemberIds
            : []);
        const excludeIds = [];
        this.viewer.getScene().traverse((object) => {
            var _a, _b, _c;
            const baseObject = object;
            if (!baseObject.uuid ||
                baseObject.uuid === primaryObject.uuid ||
                ((_a = baseObject.userData) === null || _a === void 0 ? void 0 : _a.aiAssemblyId) !== assemblyId) {
                return;
            }
            const baseMemberId = (_b = baseObject.userData) === null || _b === void 0 ? void 0 : _b.aiMemberId;
            const baseExcludeMemberIds = Array.isArray((_c = baseObject.userData) === null || _c === void 0 ? void 0 : _c.aiCollisionExcludeMemberIds)
                ? baseObject.userData.aiCollisionExcludeMemberIds
                : [];
            if (!baseMemberId) {
                return;
            }
            const hasExplicitAiExclusion = primaryExcludeMemberIds.has(baseMemberId) || baseExcludeMemberIds.includes(memberId);
            // AI generated barriers intentionally place panels, rails, hinges, and handles in contact.
            // Persisted projects use real ConnectionObjects; this explicit fallback covers the active
            // generation transaction before those project records are reloaded.
            if (hasExplicitAiExclusion) {
                excludeIds.push(baseObject.uuid);
            }
        });
        return excludeIds;
    }
    /**
     *
     * @returns UUIDs of collided objects
     */
    notifyObjectAdded(newObject, checkCollisions = false, showNotification = true) {
        const objCoProIndex = this.getObjIndex(newObject.uuid);
        if (objCoProIndex) {
            return this.notifyObjectUpdatedInternal(newObject, objCoProIndex, checkCollisions, showNotification);
        }
        return this.notifyObjectAddedInternal(newObject, checkCollisions, showNotification);
    }
    /**
     *
     * @returns UUIDs of collided objects
     */
    notifyObjectAddedInternal(newObject, checkCollisions, showNotification) {
        var _a, _b, _c;
        const boundingBox = (0, utils_3d_1.getAABBox)(newObject);
        const orientedBBox = (0, utils_3d_1.getOBB)(newObject);
        const { outerBox, directionNormal } = newObject;
        const slots = (_a = newObject.getSlots()) !== null && _a !== void 0 ? _a : [];
        const inboundSnapPts = (_b = newObject.getInboundSnappingPoints()) !== null && _b !== void 0 ? _b : [];
        const outboundSnapPts = (_c = newObject.getOutboundSnappingPoints()) !== null && _c !== void 0 ? _c : [];
        this.addObject(newObject.uuid, boundingBox, orientedBBox, outerBox, directionNormal, {
            slots,
            inboundSnapPts,
            outboundSnapPts,
        });
        // TODO: remove from here
        this.viewer.scheduleTakeScreenshot();
        if (checkCollisions) {
            return this.updateCollisions(newObject, null, showNotification);
        }
        return [];
    }
    /**
     *
     * @returns UUIDs of collided objects
     */
    notifyObjectUpdated(object, checkCollisions = false, showNotification = true) {
        const objCoProIndex = this.getObjIndex(object.uuid);
        if (objCoProIndex) {
            this.notifyObjectUpdatedInternal(object, objCoProIndex, checkCollisions, showNotification);
        }
        else {
            this.notifyObjectAddedInternal(object, checkCollisions, showNotification);
        }
    }
    updateCollisionsForObjectsAndConnections(objects, connectionObjects) {
        // updating groupboxes for objects
        objects.forEach((object) => {
            if (!object.uuidsObjectsInsertedIntoPlate) {
                object.setUuidsObjectsInsertedIntoPlate();
            }
            object.uuidsObjectsInsertedIntoPlate.forEach((uuid) => {
                const insertedObject = (0, utils_3d_1.getObjectByUUID)(Viewer_1.default.instance.getScene(), uuid);
                this.notifyObjectUpdated(insertedObject);
            });
            this.notifyObjectUpdated(object);
        });
        // updating groupboxes for fasteners
        connectionObjects.forEach((connectionObject) => {
            connectionObject === null || connectionObject === void 0 ? void 0 : connectionObject.getFasteners().forEach((fastener) => {
                this.notifyObjectUpdated(fastener);
            });
        });
        // updating collisions for objects
        objects.forEach((object) => {
            if (!object.uuidsObjectsInsertedIntoPlate) {
                object.setUuidsObjectsInsertedIntoPlate();
            }
            object.uuidsObjectsInsertedIntoPlate.forEach((uuid) => {
                const insertedObject = (0, utils_3d_1.getObjectByUUID)(Viewer_1.default.instance.getScene(), uuid);
                this.updateCollisions(insertedObject);
            });
            this.updateCollisions(object);
        });
        // updating collisions for fasteners
        connectionObjects.forEach((connectionObject) => {
            connectionObject === null || connectionObject === void 0 ? void 0 : connectionObject.getFasteners().forEach((fastener) => {
                this.updateCollisions(fastener);
            });
        });
    }
    /**
     *
     * @returns UUIDs of collided objects
     */
    notifyObjectUpdatedInternal(object, objCoProIndex, checkCollisions, showNotification) {
        var _a, _b, _c;
        const { groupBoxIndex } = objCoProIndex;
        const { objectIndex } = objCoProIndex;
        const groupBox = this.groupBoxes[groupBoxIndex];
        const boundingBox = (0, utils_3d_1.getAABBox)(object);
        const orientedBBox = (0, utils_3d_1.getOBB)(object);
        const { outerBox, directionNormal } = object;
        const slots = (_a = object.getSlots()) !== null && _a !== void 0 ? _a : [];
        const inboundSnapPts = (_b = object.getInboundSnappingPoints()) !== null && _b !== void 0 ? _b : [];
        const outboundSnapPts = (_c = object.getOutboundSnappingPoints()) !== null && _c !== void 0 ? _c : [];
        if (!groupBox.tryToUpdate(objectIndex, boundingBox, orientedBBox, outerBox, directionNormal, {
            slots,
            inboundSnapPts,
            outboundSnapPts,
        })) {
            // Firstly, add an updated object to the objects structure.
            const newObjCoProIndex = this.addObject(object.uuid, boundingBox, orientedBBox, outerBox, directionNormal, {
                slots,
                inboundSnapPts,
                outboundSnapPts,
            });
            // Reassign all collision references to the new object location.
            this.updateIntersectionsIndices(objCoProIndex, newObjCoProIndex, true);
            // Remove the object from old location.
            this.removeObject(objCoProIndex);
        }
        let result = [];
        if (checkCollisions) {
            result = this.updateCollisions(object, null, showNotification);
        }
        // TODO: remove from here
        this.viewer.scheduleTakeScreenshot();
        return result;
    }
    notifyObjectRemoved(object) {
        const objCoProIndex = this.getObjIndex(object.uuid);
        if (objCoProIndex) {
            this.removeCollisions(object, objCoProIndex);
            this.removeObject(objCoProIndex);
        }
        // TODO: remove from here
        this.viewer.scheduleTakeScreenshot();
    }
    /**
     * @description Search for all collisions (or first found collision)
     *
     * @param testBox Oriented Bounding Box to test collisions for
     * @param excludeIDs Array of object UUIDs to exclude from testing
     * @param firstFoundCollisionOnly Used to specify that only first found collision should be returned
     *
     * @returns collided objects coProIndex array
     */
    testCollisions(object, testBox, { excludeIDs = new Set(), precision = 0.001, firstFoundCollisionOnly = false, useObbInsteadOfMeshCollision = false, } = {}) {
        let testResult = false;
        const indices = [];
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            const test = this.groupBoxes[i].testIntersection(object, testBox, {
                excludeIDs,
                precision,
                firstFoundCollisionOnly,
                useObbInsteadOfMeshCollision,
            });
            if (test.result === true) {
                testResult = true;
                for (let k = 0; k < test.objects.length; k += 1) {
                    indices.push({
                        groupBoxIndex: i,
                        objectIndex: test.objects[k],
                    });
                }
            }
        }
        return { result: testResult, indices };
    }
    /**
     * @description Search for the closest collision to a position in the direction
     *
     * @param object object to check distance from it to collision
     * @param direction direction of the object to measure distance to collision
     *
     * @returns collided object coProIndex
     */
    getClosestCollision(object, direction, excludeIDs = new Set()) {
        let testResult = false;
        let groupBoxIndex = null;
        let objectIndex = null;
        let minDist = Infinity;
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            const test = this.groupBoxes[i].getClosest(object, direction, excludeIDs);
            if (test.result && test.distance < minDist) {
                minDist = test.distance;
                objectIndex = test.objIndex;
                testResult = test.result;
                groupBoxIndex = i;
            }
        }
        return {
            result: testResult,
            index: { groupBoxIndex: groupBoxIndex, objectIndex: objectIndex, distanceToCollision: minDist },
        };
    }
    /**
     * @description Test collision for angled extrusion
     *
     * @param object object to check distance from it to collision
     * @param direction direction of the angled extrusion
     * @param excludeID uuid of an object to exclude from testing
     *
     * @returns collision point
     *
     * collided object normal
     *
     * distance to collision
     *
     * uuid of collided object
     */
    testAngledCollision(object, direction, excludeIDs = new Set()) {
        let testResult = false;
        const coProIndex = { groupBoxIndex: 0, objectIndex: 0 };
        let slot;
        let distance = null;
        let uuid = null;
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            if ((0, utils_3d_1.getAABBox)(object).intersectsBox(this.groupBoxes[i].getGroupBox()) === false) {
                // eslint-disable-next-line no-continue
                continue;
            }
            const test = this.groupBoxes[i].getClosest(object, direction, excludeIDs);
            if (test.result === true) {
                testResult = true;
                if (test.distance && (distance === null || test.distance < distance)) {
                    distance = test.distance;
                    uuid = test.uuid;
                    slot = test.slot;
                    coProIndex.groupBoxIndex = i;
                    if (test.objIndex) {
                        coProIndex.objectIndex = test.objIndex;
                    }
                }
            }
        }
        return {
            result: testResult,
            coProIndex,
            point: slot === null || slot === void 0 ? void 0 : slot.surfacePoint,
            normal: slot === null || slot === void 0 ? void 0 : slot.normal,
            distance,
            uuid,
        };
    }
    /**
     * @description Get bounding box of a group box by it's index
     *
     * Index is not checked for overflow or underflow
     * @param index index of a group box
     *
     * @returns bounding box
     */
    getGroupBox(index) {
        return this.groupBoxes[index].getGroupBox();
    }
    getGroupBoxes() {
        return this.groupBoxes;
    }
    getObjBox(index) {
        return this.groupBoxes[index.groupBoxIndex].getObjBox(index.objectIndex);
    }
    getObjectOBB(index) {
        return this.groupBoxes[index.groupBoxIndex].getObjOBB(index.objectIndex);
    }
    getObjNormal(index) {
        return this.groupBoxes[index.groupBoxIndex].getObjNormal(index.objectIndex);
    }
    getObjSlots(index) {
        return this.groupBoxes[index.groupBoxIndex].getSlots(index.objectIndex);
    }
    getObjInboundSnapPts(index) {
        return this.groupBoxes[index.groupBoxIndex].getInboundSnapPts(index.objectIndex);
    }
    getObjOutboundSnapPts(index) {
        return this.groupBoxes[index.groupBoxIndex].getOutboundSnapPts(index.objectIndex);
    }
    getObjUUID(index) {
        return this.groupBoxes[index.groupBoxIndex].getUUID(index.objectIndex);
    }
    getObjIndex(uuid) {
        let index = null;
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            index = this.groupBoxes[i].tryToFind(uuid);
            if (index !== null) {
                return { groupBoxIndex: i, objectIndex: index };
            }
        }
        return null;
    }
    getSlot(index, slotIndex) {
        return this.groupBoxes[index.groupBoxIndex].getSlot(index.objectIndex, slotIndex);
    }
    getSnappingPoint(index, pointIndex) {
        return this.groupBoxes[index.groupBoxIndex].getPoint(index.objectIndex, pointIndex);
    }
    clearTemporaryExcluded() {
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            this.groupBoxes[i].clearTemporaryExcluded();
        }
    }
    setTemporaryExcluded(excludeIDs) {
        for (let i = 0; i < this.groupBoxes.length; i += 1) {
            this.groupBoxes[i].setTemporaryExcluded(excludeIDs);
        }
    }
    getGBoxByPoint(point) {
        for (let groupBoxIndex = 0; groupBoxIndex < this.groupBoxes.length; groupBoxIndex += 1) {
            if (this.groupBoxes[groupBoxIndex].getGroupBox().containsPoint(point)) {
                return groupBoxIndex;
            }
        }
        return null;
    }
    removeCollisions(object, objectIndex = null) {
        const primaryObject = object;
        const primaryObjectUUID = primaryObject.uuid;
        const primaryObjIdx = objectIndex !== null && objectIndex !== void 0 ? objectIndex : this.getObjIndex(primaryObjectUUID);
        if (!primaryObjIdx) {
            return;
        }
        const intersections = this.groupBoxes[primaryObjIdx.groupBoxIndex].getIntersections(primaryObjIdx.objectIndex);
        for (let i = 0; i < intersections.length; i += 1) {
            const collidedObjIdx = intersections[i];
            // Remove intersection with the primary object from neighbor object and
            // hide neighbor object's collision box if it has no more intersections
            if (this.groupBoxes[collidedObjIdx.groupBoxIndex].removeIntersection(collidedObjIdx.objectIndex, primaryObjIdx)) {
                const collidedObjectUUID = this.getObjUUID(collidedObjIdx);
                const collidedObject = (0, utils_3d_1.getObjectByUUID)(this.viewer.getScene(), collidedObjectUUID);
                collidedObject.hideCollisionBox();
            }
        }
        // Remove all intersections from the primary object
        this.groupBoxes[primaryObjIdx.groupBoxIndex].removeAllIntersections(primaryObjIdx.objectIndex);
        // Hide collision box of the primary object
        primaryObject.hideCollisionBox();
    }
    /**
     *
     * @returns UUIDs of collided objects
     */
    updateCollisions(object, objectIndex = null, showNotification = true) {
        const primaryObject = object;
        const primaryObjectUUID = primaryObject.uuid;
        const primaryObjIdx = objectIndex !== null && objectIndex !== void 0 ? objectIndex : this.getObjIndex(primaryObjectUUID);
        if (!primaryObjIdx) {
            return [];
        }
        const selectedUUIDs = new Set(primaryObject.getCollisionExcludeIds());
        const connectedUUIDs = new Set(primaryObject.getConnectionExcludeIDs());
        const aiExcludeIds = this.getAiCollisionExcludeIds(primaryObject);
        aiExcludeIds.forEach((uuid) => connectedUUIDs.add(uuid));
        this.viewer.selectedObjects.forEach((item) => {
            selectedUUIDs.add(item.uuid);
        });
        this.viewer
            .getDesignProMain()
            .connectionManager.getRelatedConnectionObjects(primaryObject)
            .forEach((connectionObject) => {
            connectionObject.getFasteners().forEach((fastener) => {
                connectedUUIDs.add(fastener.uuid);
            });
        });
        const previousIntersections = this.groupBoxes[primaryObjIdx.groupBoxIndex].getIntersections(primaryObjIdx.objectIndex);
        const previousIntersectionsWithoutExcluded = previousIntersections.filter((intersection) => {
            return !selectedUUIDs.has(this.getObjUUID(intersection));
        });
        const test = this.testCollisions(primaryObject, this.getObjectOBB(primaryObjIdx), {
            excludeIDs: new Set([...selectedUUIDs, ...connectedUUIDs]),
        });
        if (test.result || previousIntersections.length > previousIntersectionsWithoutExcluded.length) {
            previousIntersectionsWithoutExcluded
                .filter((intersection) => {
                return test.indices.every((index) => {
                    return index.groupBoxIndex !== intersection.groupBoxIndex || index.objectIndex !== intersection.objectIndex;
                });
            })
                .forEach((intersection) => {
                // Remove intersection with the primary object from a neighbor object
                this.groupBoxes[intersection.groupBoxIndex].removeIntersection(intersection.objectIndex, primaryObjIdx);
                // Hide collision box of a neighbor object if it has no more intersections
                if (!this.groupBoxes[intersection.groupBoxIndex].hasIntersections(intersection.objectIndex)) {
                    const neighborObjectUUID = this.getObjUUID(intersection);
                    const neighborObject = (0, utils_3d_1.getObjectByUUID)(this.viewer.getScene(), neighborObjectUUID);
                    neighborObject.hideCollisionBox();
                }
                // Remove intersection with a neighbor object from the primary object
                this.groupBoxes[primaryObjIdx.groupBoxIndex].removeIntersection(primaryObjIdx.objectIndex, intersection);
            });
            test.indices
                .filter((collision) => {
                return previousIntersectionsWithoutExcluded.every((intersection) => {
                    return (intersection.groupBoxIndex !== collision.groupBoxIndex ||
                        intersection.objectIndex !== collision.objectIndex);
                });
            })
                .forEach((collision) => {
                // Show collision box of collided neighbor object if this is the first its collision
                if (!this.groupBoxes[collision.groupBoxIndex].hasIntersections(collision.objectIndex)) {
                    const collidedObjectUUID = this.getObjUUID(collision);
                    const collidedObject = (0, utils_3d_1.getObjectByUUID)(this.viewer.getScene(), collidedObjectUUID);
                    collidedObject.showCollisionBox();
                }
                // Add intersections between collided neighbor object and the primary object
                this.groupBoxes[primaryObjIdx.groupBoxIndex].addIntersection(primaryObjIdx.objectIndex, collision);
                this.groupBoxes[collision.groupBoxIndex].addIntersection(collision.objectIndex, primaryObjIdx);
            });
            // Show collision box of the primary object
            primaryObject.showCollisionBox();
            if (showNotification) {
                const collidedItemNumbers = [Number(primaryObject.partDetails.itemNumber)];
                let areFastenersIntersectedOnly = (0, utils_3d_1.isFastener)(primaryObject);
                this.groupBoxes[primaryObjIdx.groupBoxIndex]
                    .getIntersections(primaryObjIdx.objectIndex)
                    .forEach((collision) => {
                    const collidedObjectUUID = this.getObjUUID(collision);
                    const collidedObject = (0, utils_3d_1.getObjectByUUID)(this.viewer.getScene(), collidedObjectUUID);
                    collidedItemNumbers.push(Number(collidedObject.partDetails.itemNumber));
                    areFastenersIntersectedOnly && (areFastenersIntersectedOnly = (0, utils_3d_1.isFastener)(collidedObject));
                });
                collidedItemNumbers.sort((a, b) => a - b);
                const firstMessagePart = areFastenersIntersectedOnly ? 'Fasteners' : 'Parts';
                const secondMessagePart = areFastenersIntersectedOnly
                    ? 'interfere with each other. Please change fasteners in connections causing the conflict.'
                    : 'interfere with each other. Please move them apart.';
                document.body.dispatchEvent((0, events_1.dispatchNotificationEvent)({
                    message: `${firstMessagePart} '${[...new Set(collidedItemNumbers)].join("', '")}' ${secondMessagePart}`,
                    type: 'error',
                }));
            }
            return this.groupBoxes[primaryObjIdx.groupBoxIndex]
                .getIntersections(primaryObjIdx.objectIndex)
                .map((collision) => this.getObjUUID(collision));
        }
        this.removeCollisions(primaryObject, primaryObjIdx);
        return [];
    }
    addObject(uuid, boundingBox, orientedBBox, outerBox, normal, snappingInfo) {
        let groupBoxIndex = 0;
        let objectIndex = 0;
        let tryToAddResult = { result: false, objectIndex: 0 };
        if (this.groupBoxes && this.groupBoxes.length !== 0) {
            let minVolume = null;
            let minIndex = null;
            let currVolume = 0;
            let startIndex = 0;
            for (let groupBoxIdx = 0; groupBoxIdx < this.groupBoxes.length; groupBoxIdx += 1) {
                minVolume = this.groupBoxes[groupBoxIdx].testNewVolume(boundingBox);
                if (minVolume !== null) {
                    startIndex = groupBoxIdx + 1;
                    minIndex = groupBoxIdx;
                    break;
                }
            }
            if (minIndex !== null) {
                for (let groupBoxIdx = startIndex; groupBoxIdx < this.groupBoxes.length; groupBoxIdx += 1) {
                    currVolume = this.groupBoxes[groupBoxIdx].testNewVolume(boundingBox);
                    if (currVolume < minVolume || minVolume === null) {
                        minVolume = currVolume;
                        minIndex = groupBoxIdx;
                    }
                }
                tryToAddResult = this.groupBoxes[minIndex].tryToAdd(uuid, boundingBox, orientedBBox, outerBox, normal, snappingInfo);
                groupBoxIndex = minIndex;
                objectIndex = tryToAddResult.objectIndex;
            }
        }
        if (!tryToAddResult.result) {
            // Create a new group box
            this.groupBoxes.push(new GroupBox_3d_1.GroupBox(uuid, boundingBox, orientedBBox, outerBox, normal, snappingInfo));
            groupBoxIndex = this.groupBoxes.length - 1;
            objectIndex = 0;
            // Then add it to the scene
            this.viewer.getScene().add(this.groupBoxes[groupBoxIndex].getGroupBoxMesh());
        }
        return { groupBoxIndex, objectIndex };
    }
    removeObject(objectToRemoveIndex) {
        const objectToRemoveGroupBoxIndex = objectToRemoveIndex.groupBoxIndex;
        const groupBox = this.groupBoxes[objectToRemoveGroupBoxIndex];
        groupBox.removeObject(objectToRemoveIndex.objectIndex);
        if (groupBox.isEmpty()) {
            this.viewer.getScene().remove(groupBox.getGroupBoxMesh());
            this.groupBoxes.splice(objectToRemoveGroupBoxIndex, 1);
            this.shiftIntersectionsGroupBoxesIndicesFollowingBy(objectToRemoveIndex.groupBoxIndex);
        }
        else {
            this.shiftIntersectionsObjectsIndicesFollowingBy(objectToRemoveIndex);
        }
    }
    shiftIntersectionsGroupBoxesIndicesFollowingBy(removedGroupBoxIndex) {
        this.groupBoxes.forEach((groupBox) => {
            groupBox.shiftIntersectionsGroupBoxesIndicesFollowingBy(removedGroupBoxIndex);
        });
    }
    shiftIntersectionsObjectsIndicesFollowingBy(removedObjectIndex) {
        this.groupBoxes.forEach((groupBox) => {
            groupBox.shiftIntersectionsObjectsIndicesFollowingBy(removedObjectIndex);
        });
    }
    updateIntersectionsIndices(oldIndex, newIndex, mutualUpdate = false) {
        const oldGroupBoxIndex = oldIndex.groupBoxIndex;
        const oldObjectIndex = oldIndex.objectIndex;
        const newGroupBoxIndex = newIndex.groupBoxIndex;
        const newObjectIndex = newIndex.objectIndex;
        this.groupBoxes.forEach((groupBox, groupBoxIndex) => {
            groupBox.objectsData.forEach((objectData, objectIndex) => {
                objectData.intersectsWith.forEach((intersection) => {
                    if (intersection.groupBoxIndex === oldGroupBoxIndex && intersection.objectIndex === oldObjectIndex) {
                        if (groupBoxIndex === newGroupBoxIndex && objectIndex === newObjectIndex) {
                            return;
                        }
                        intersection.groupBoxIndex = newGroupBoxIndex;
                        intersection.objectIndex = newObjectIndex;
                        if (mutualUpdate) {
                            this.groupBoxes[newGroupBoxIndex].addIntersection(newObjectIndex, { groupBoxIndex, objectIndex });
                        }
                    }
                });
            });
        });
    }
}
exports.default = CollisionProcessor;
